import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, UserMinus, Trophy, BookOpen } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { TrustBadges } from "@/components/TrustBadges";
import { WishlistSection } from "@/components/WishlistSection";
import { pageHead, jsonLd, SITE_URL } from "@/lib/seo";

export const Route = createFileRoute("/u/$id")({
  loader: async ({ params }) => {
    try {
      const { data } = await supabase.rpc("get_public_profiles");
      const p = (data ?? []).find((x: any) => x.id === params.id) ?? null;
      return { profile: p };
    } catch {
      return { profile: null };
    }
  },
  head: ({ params, loaderData }) => {
    const p = loaderData?.profile as { full_name: string | null; bio: string | null } | null | undefined;
    const name = p?.full_name?.trim() || "Reader";
    const title = `${name} — reader profile on Book Swap`;
    const description = (
      p?.bio?.trim() || `See the books ${name} is sharing on Book Swap, their exchange history and badges, and message them about a swap.`
    ).slice(0, 160);
    return {
      ...pageHead({ path: `/u/${params.id}`, title, description, type: "profile" }),
      scripts: [
        jsonLd({
          "@context": "https://schema.org",
          "@type": "ProfilePage",
          url: `${SITE_URL}/u/${params.id}`,
          isPartOf: { "@type": "WebSite", name: "Book Swap", url: SITE_URL },
          mainEntity: {
            "@type": "Person",
            name,
            ...(p?.bio?.trim() ? { description: p.bio.trim() } : {}),
            url: `${SITE_URL}/u/${params.id}`,
          },
        }),
      ],
    };
  },
  component: PublicUserProfile,
});

type PP = { id: string; full_name: string | null; avatar_url: string | null; trust_score: number | null; books_listed: number | null; verified?: boolean | null; completed_swaps?: number | null; response_rate?: number | null; bio?: string | null; favorite_quote?: string | null; strikes?: number | null };
type Book = { id: string; title: string; cover_url: string | null };
type Badge = { mission_id: string; earned_at: string };

function PublicUserProfile() {
  const { id } = Route.useParams();
  const { user } = useAuth();
  const [profile, setProfile] = useState<PP | null>(null);
  const [books, setBooks] = useState<Book[]>([]);
  const [badges, setBadges] = useState<Badge[]>([]);
  const [followers, setFollowers] = useState(0);
  const [following, setFollowing] = useState(0);
  const [iFollow, setIFollow] = useState(false);
  const [busy, setBusy] = useState(false);

  async function load() {
    const [{ data: p }, { data: extra }, { data: bs }, { data: bg }, { data: fc }, { data: fc2 }] = await Promise.all([
      supabase.from("user_stats").select("id,full_name,avatar_url,trust_score,books_listed").eq("id", id).maybeSingle(),
      supabase.from("public_profiles").select("verified,completed_swaps,response_rate,bio,favorite_quote,strikes").eq("id", id).maybeSingle(),
      supabase.from("books").select("id,title,cover_url").eq("user_id", id).order("created_at", { ascending: false }).limit(12),
      supabase.rpc("get_public_badges" as any, { target_user_id: id }),
      supabase.rpc("get_follower_count" as any, { target_user_id: id }),
      supabase.rpc("get_following_count" as any, { target_user_id: id }),
    ]);
    setProfile({ ...(p as any), ...(extra as any) } as PP);
    setBooks((bs as Book[]) ?? []);
    setBadges((bg as Badge[]) ?? []);
    setFollowers((fc as number) ?? 0);
    setFollowing((fc2 as number) ?? 0);
    if (user) {
      const { data } = await supabase.from("follows").select("follower_id").eq("follower_id", user.id).eq("following_id", id).maybeSingle();
      setIFollow(!!data);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, user]);

  async function toggleFollow() {
    if (!user) return toast.error("Sign in to follow readers.");
    setBusy(true);
    if (iFollow) {
      await supabase.from("follows").delete().eq("follower_id", user.id).eq("following_id", id);
      setIFollow(false); setFollowers((n) => Math.max(0, n - 1));
    } else {
      const { error } = await supabase.from("follows").insert({ follower_id: user.id, following_id: id });
      if (error) toast.error("Could not follow."); else { setIFollow(true); setFollowers((n) => n + 1); }
    }
    setBusy(false);
  }

  if (!profile) return <div className="min-h-screen bg-background"><Navbar /><p className="p-12 text-center text-muted-foreground">Loading…</p></div>;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-4xl px-4 py-10">
        <div className="flex flex-wrap items-center gap-6 rounded-3xl bg-card p-6 shadow-[var(--shadow-card)]">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary-soft text-2xl font-bold text-primary">
            {profile.avatar_url ? <img src={profile.avatar_url} alt="" className="h-full w-full rounded-full object-cover" /> : (profile.full_name?.[0] ?? "?")}
          </div>
          <div className="flex-1">
            <h1 className="font-serif text-3xl font-semibold">{profile.full_name ?? "A neighbor"}</h1>
            <div className="mt-2">
              <TrustBadges compact data={{
                verified: profile.verified,
                completed_swaps: profile.completed_swaps,
                response_rate: profile.response_rate,
                strikes: profile.strikes,
              }} />
            </div>
            <div className="mt-3 flex flex-wrap gap-4 text-sm text-muted-foreground">
              <span><strong className="text-foreground">{profile.books_listed ?? 0}</strong> books</span>
              <span><strong className="text-foreground">{followers}</strong> followers</span>
              <span><strong className="text-foreground">{following}</strong> following</span>
            </div>
            {profile.favorite_quote && (
              <p className="mt-3 border-l-2 border-primary/40 pl-3 font-serif text-sm italic text-muted-foreground">
                "{profile.favorite_quote}"
              </p>
            )}
            {profile.bio && <p className="mt-2 text-sm text-foreground/90">{profile.bio}</p>}
          </div>
          {user && user.id !== id && (
            <Button onClick={toggleFollow} disabled={busy} variant={iFollow ? "outline" : "default"}>
              {iFollow ? <><UserMinus className="mr-2 h-4 w-4" />Unfollow</> : <><UserPlus className="mr-2 h-4 w-4" />Follow</>}
            </Button>
          )}
        </div>

        {badges.length > 0 && (
          <section className="mt-8">
            <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold"><Trophy className="h-4 w-4 text-primary" /> Badges</h2>
            <div className="flex flex-wrap gap-2">
              {badges.map((b) => <span key={b.mission_id} className="rounded-full bg-primary-soft px-3 py-1 text-xs font-semibold text-primary-dark">🏅 {b.mission_id}</span>)}
            </div>
          </section>
        )}

        <WishlistSection userId={id} ownerName={profile.full_name ? `${profile.full_name}'s` : undefined} />

        <section className="mt-8">
          <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold"><BookOpen className="h-4 w-4 text-primary" /> Books listed</h2>
          {books.length === 0 ? <p className="text-sm text-muted-foreground">No books yet.</p> : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {books.map((b) => (
                <Link key={b.id} to="/book/$id" params={{ id: b.id }} className="block">
                  <div className="aspect-[3/4] overflow-hidden rounded-lg bg-primary-soft">
                    {b.cover_url ? <img src={b.cover_url} alt={b.title} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><BookOpen className="h-8 w-8 text-primary/40" /></div>}
                  </div>
                  <p className="mt-1 truncate text-xs">{b.title}</p>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
